import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { Car, AlertTriangle, X, Upload, Camera } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LICENSE_CLASSES = [
  { value: 'G', label: 'Class G - Full License' },
  { value: 'G2', label: 'Class G2 - Probationary' },
  { value: 'G1', label: 'Class G1 - Learner' },
  { value: 'A', label: 'Class A - Tractor Trailer' },
  { value: 'B', label: 'Class B - Bus' },
  { value: 'C', label: 'Class C - Regular Bus' },
  { value: 'D', label: 'Class D - Trucks' },
  { value: 'E', label: 'Class E - School Bus' },
  { value: 'F', label: 'Class F - Ambulance' },
  { value: 'M', label: 'Class M - Motorcycle' },
  { value: 'other', label: 'Other' }
];

export function DrivingLicensePrompt({ user, onComplete }) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [licenseData, setLicenseData] = useState({
    license_type: 'Driver License',
    license_number: '',
    license_class: '',
    state: 'Ontario',
    issue_date: '',
    expiry_date: ''
  });
  
  const [documentFile, setDocumentFile] = useState(null);

  useEffect(() => {
    // Don't prompt office staff for driving license
    const officeRoles = ['estimator', 'project_coordinator', 'manager', 'senior_manager', 
                        'general_manager', 'director', 'accounts', 'purchaser', 
                        'accounts_purchaser', 'company_admin', 'super_admin'];
    
    if (user && !hasChecked && !officeRoles.includes(user.role)) {
      checkForDrivingLicense();
    } else if (officeRoles.includes(user?.role)) {
      setHasChecked(true); // Skip checking for office staff
    }
  }, [user, hasChecked]);

  const checkForDrivingLicense = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/my-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const profile = await response.json();
        
        // Check if user has a driving license
        const hasDrivingLicense = profile.licenses?.some(
          l => l.license_type?.toLowerCase().includes('driver') || 
               l.license_type?.toLowerCase().includes('driving')
        );
        
        // Check if user has permanently dismissed this prompt
        const permanentlyDismissed = localStorage.getItem('driving_license_prompt_permanent_dismiss');
        
        // Check if user dismissed this prompt recently (within 7 days)
        const dismissed = localStorage.getItem('driving_license_prompt_dismissed');
        const dismissedRecently = dismissed && 
          (Date.now() - new Date(dismissed).getTime()) < 7 * 24 * 60 * 60 * 1000;
        
        if (!hasDrivingLicense && !dismissedRecently && !permanentlyDismissed) {
          setShowPrompt(true);
        }
      }
    } catch (error) {
      console.error('Failed to check driving license:', error);
    } finally {
      setHasChecked(true);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('driving_license_prompt_dismissed', new Date().toISOString());
    setShowPrompt(false);
  };

  const handleDismissPermanently = () => {
    localStorage.setItem('driving_license_prompt_permanent_dismiss', 'true');
    setShowPrompt(false);
  };

  const handleAddLicense = async () => {
    if (!licenseData.license_number || !licenseData.expiry_date) {
      toast.error('Please fill in license number and expiry date');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // First, add the license
      const response = await fetch(`${API_URL}/api/my-profile/licenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...licenseData,
          license_type: `Driver License (${licenseData.license_class || 'G'})`
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // If there's a document to upload
        if (documentFile && result.license?.id) {
          setUploading(true);
          const formData = new FormData();
          formData.append('file', documentFile);
          
          await fetch(`${API_URL}/api/my-profile/licenses/${result.license.id}/upload`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
        }
        
        toast.success('Driving license added successfully!');
        setShowAddDialog(false);
        setShowPrompt(false);
        onComplete?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add license');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File must be less than 5MB');
        return;
      }
      setDocumentFile(file);
    }
  };

  if (!showPrompt) return null;

  return (
    <>
      {/* Floating Prompt Banner */}
      <div className="fixed bottom-4 right-4 max-w-sm bg-amber-50 border border-amber-300 rounded-lg shadow-lg p-4 z-40 animate-slide-up" data-testid="driving-license-prompt">
        <div className="flex items-start gap-3">
          <div className="bg-amber-100 p-2 rounded-full">
            <Car className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-amber-900">Add Your Driving License</h4>
            <p className="text-sm text-amber-700 mt-1">
              Please add your driving license for work-related transportation records.
            </p>
            <div className="flex gap-2 mt-3">
              <Button 
                size="sm" 
                onClick={() => setShowAddDialog(true)}
                data-testid="add-license-prompt-btn"
              >
                Add Now
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={handleDismiss}
              >
                Later
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={handleDismissPermanently}
                className="text-xs"
              >
                Don't Ask Again
              </Button>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-amber-400 hover:text-amber-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Add License Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="w-5 h-5" />
              Add Driving License
            </DialogTitle>
            <DialogDescription>
              Enter your driving license details for company records
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>License Class *</Label>
              <Select 
                value={licenseData.license_class} 
                onValueChange={(v) => setLicenseData(d => ({ ...d, license_class: v }))}
              >
                <SelectTrigger data-testid="license-class-select">
                  <SelectValue placeholder="Select license class" />
                </SelectTrigger>
                <SelectContent>
                  {LICENSE_CLASSES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>License Number *</Label>
              <Input
                value={licenseData.license_number}
                onChange={(e) => setLicenseData(d => ({ ...d, license_number: e.target.value }))}
                placeholder="e.g., A1234-56789-01234"
                data-testid="license-number-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={licenseData.issue_date}
                  onChange={(e) => setLicenseData(d => ({ ...d, issue_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Expiry Date *</Label>
                <Input
                  type="date"
                  value={licenseData.expiry_date}
                  onChange={(e) => setLicenseData(d => ({ ...d, expiry_date: e.target.value }))}
                  data-testid="license-expiry-input"
                />
              </div>
            </div>

            <div>
              <Label>Province/State</Label>
              <Input
                value={licenseData.state}
                onChange={(e) => setLicenseData(d => ({ ...d, state: e.target.value }))}
                placeholder="Ontario"
              />
            </div>

            {/* Document Upload */}
            <Card className="bg-gray-50">
              <CardContent className="p-4">
                <Label className="text-sm font-medium">Upload License Photo (Optional)</Label>
                <p className="text-xs text-gray-500 mb-2">Take a photo or upload a scan of your license</p>
                
                {documentFile ? (
                  <div className="flex items-center justify-between bg-white p-2 rounded border">
                    <span className="text-sm truncate">{documentFile.name}</span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setDocumentFile(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg hover:bg-gray-100 transition-colors">
                    <Camera className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-600">Take Photo or Upload</span>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      capture="environment"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                )}
              </CardContent>
            </Card>

            <div className="flex gap-2 pt-4">
              <Button 
                className="flex-1" 
                onClick={handleAddLicense}
                disabled={saving || uploading}
                data-testid="save-license-btn"
              >
                {saving ? 'Saving...' : uploading ? 'Uploading...' : 'Save License'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowAddDialog(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </>
  );
}

export default DrivingLicensePrompt;
